import React from 'react';
import styled from 'styled-components';
import { LucideIcon } from 'lucide-react';
import { ThemeColors, Spacing, Typography, BorderRadius, Shadows, Transitions } from '../../styles/ThemeStyles';
import { getAriaLabel, keyboardHandlers } from '../../utils/accessibility';

interface DashboardCardProps {
  id: string;
  label: string;
  description: string;
  icon: LucideIcon;
  onClick: () => void;
  variant?: 'desktop' | 'mobile';
}

const StyledCard = styled.button<{ variant: string }>`
  background-color: ${ThemeColors.background.paper};
  border-radius: ${BorderRadius.lg};
  overflow: hidden;
  box-shadow: ${Shadows.sm};
  transition: transform ${Transitions.duration.short} ${Transitions.easing.easeInOut},
              box-shadow ${Transitions.duration.short} ${Transitions.easing.easeInOut};
  cursor: pointer;
  border: none;
  padding: 0;
  width: 100%;
  text-align: left;
  height: 300px;
  
  &:hover {
    transform: translateY(-4px);
    box-shadow: ${Shadows.lg};
  }
  
  &:active {
    transform: ${props => props.variant === 'mobile' ? 'scale(0.98)' : 'translateY(-2px)'};
  }
  
  &:focus {
    outline: none;
    box-shadow: 0 0 0 2px ${ThemeColors.focus.ring};
  }
`;

const CardTop = styled.div`
  background: linear-gradient(135deg, #4CAF50, #388E3C);
  padding: ${Spacing.xl};
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  height: 200px;
  position: relative;
`;

const IconContainer = styled.div<{ variant: string }>`
  color: white;
  position: absolute;
  top: ${Spacing.lg};
  right: ${Spacing.lg};
  opacity: 0.9;
`;

const CardTitle = styled.h3<{ variant: string }>`
  font-family: ${Typography.fontFamily.secondary};
  font-size: ${props => props.variant === 'desktop' ? Typography.fontSize.xl : Typography.fontSize.lg};
  font-weight: ${Typography.fontWeight.regular};
  color: white;
  text-align: left;
  margin: 0;
  position: absolute;
  bottom: ${Spacing.lg};
  left: ${Spacing.lg};
`;

const CardBottom = styled.div`
  padding: ${Spacing.lg};
  background-color: #F5F5F5;
  height: 100px;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const CardDescription = styled.p<{ variant: string }>`
  font-family: ${Typography.fontFamily.primary};
  font-size: ${props => props.variant === 'desktop' ? Typography.fontSize.sm : Typography.fontSize.xs};
  font-weight: ${Typography.fontWeight.regular};
  color: #666666;
  text-align: center;
  margin: 0;
  line-height: ${Typography.lineHeight.relaxed};
`;

export const DashboardCard: React.FC<DashboardCardProps> = ({
  id,
  label,
  description,
  icon: IconComponent,
  onClick,
  variant = 'desktop'
}) => {
  return (
    <StyledCard 
      variant={variant} 
      onClick={onClick}
      onKeyDown={keyboardHandlers.onActivate(onClick)}
      aria-label={getAriaLabel('Navigate to', label)}
      aria-describedby={`${id}-description`}
      role="button"
      tabIndex={0}
    >
      <CardTop>
        <IconContainer variant={variant}>
          <IconComponent 
            size={variant === 'desktop' ? 48 : 36} 
            strokeWidth={1.5} 
            aria-hidden="true"
          />
        </IconContainer>
        <CardTitle variant={variant}>{label}</CardTitle>
      </CardTop>
      <CardBottom>
        <CardDescription 
          variant={variant} 
          id={`${id}-description`}
          aria-hidden="true"
        >
          {description}
        </CardDescription>
      </CardBottom>
    </StyledCard>
  );
};
